import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import Navigation from '@/components/navigation';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useQuery } from '@tanstack/react-query';
import RealTimeCelebration from '@/components/real-time-celebration';
import { useToast } from '@/hooks/use-toast';
import { useWaterLogging } from '@/hooks/useWaterLogging';
import { useHydrationSync } from '@/hooks/useHydrationSync';
import { useActiveHours } from '@/hooks/useActiveHours';
import NewsCarousel from '@/components/NewsCarousel';
import FeaturedArticleOverlay from '@/components/FeaturedArticleOverlay';
import ErrorBoundary from '@/components/error-boundary';
import MainstageSlideCard from '@/components/mainstage-slide-card';
import WavyBadge from '@/components/WavyBadge';
import SwygWaveMark from '@/components/SwygWaveMark';
import { calculateActiveHours } from '@/lib/goalCalculations';
import {
  resolveDailyTargetMl,
  resolveGoalLitres,
  resolveFrequencyGoal,
  resolveVolumePerReminder,
} from '@/lib/hydrationGoals';
import { getDynamicScoreParts } from '@shared/sipsScore';
import {
  completedReminderIntervals,
  pendingIntervalsSinceDeadline,
  reconcileFrequencyTracking,
  zonedMinutesOfDay,
} from '@shared/reminderIntervals';
import { generateInsights } from '@/lib/insights';
import { buildWeekAnalytics } from '@/lib/weekAnalytics';
import { Carousel, CarouselContent, CarouselItem, CarouselApi } from '@/components/ui/carousel';
import { calculateBadges, getTimeBadge, TIME_BADGES } from '@shared/client-types';
import { refetchHydrationData } from '@/lib/refetchHydrationData';
import { getDisplayName } from '@/lib/displayName';
import BrandHeader from '@/components/BrandHeader';

function formatElapsedSince(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes.toString().padStart(2, '0')}m ${seconds
      .toString()
      .padStart(2, '0')}s`;
  }
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

export default function Dashboard() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { toast } = useToast();
  const { logWater, isLogging } = useWaterLogging();
  const { isWithinActiveHours } = useActiveHours();

  // Keep Mainstage in sync with notifications, other tabs, and cross-device updates
  useHydrationSync({ showNotificationToasts: true, label: 'Dashboard' });

  const [showDrinkDialog, setShowDrinkDialog] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showRipple, setShowRipple] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [selectedSlide, setSelectedSlide] = useState(0);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const recapListRef = useRef<HTMLDivElement>(null);
  const recapUserPausedRef = useRef(false);
  const recapPauseTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Use local date for "today" to avoid timezone issues (e.g. 1 AM IST being previous day in UTC)
  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time

  // Simple redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      window.location.href = '/login';
    }
  }, [isAuthenticated, isLoading]);

  // Fetch daily stats — short staleTime + focus refetch for cross-device sync
  const { data: dailyStats, isLoading: statsLoading } = useQuery<any>({
    queryKey: ['/api/stats/daily', { date: today }],
    retry: false,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000, // Poll while Mainstage is open (desktop ↔ phone)
  });

  // Fetch water logs
  const {
    data: waterLogs,
    isLoading: logsLoading,
    isError: logsError,
  } = useQuery<any[]>({
    queryKey: ['/api/water-intake', { date: today }],
    retry: false,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000,
  });

  // Fetch tracking data for times drank/skipped
  const { data: trackingData, isError: trackingError } = useQuery<any>({
    queryKey: ['/api/onesignal/tracking', { date: today }],
    retry: false,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000,
  });

  // Fetch user profile for calculating expected reminders per day
  const { data: userProfile } = useQuery<any>({
    queryKey: ['/api/profile'],
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const weekStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toLocaleDateString('en-CA');
  }, []);

  const { data: weeklyStats } = useQuery<any[]>({
    queryKey: ['/api/stats/weekly', { startDate: weekStart, endDate: today }],
    retry: false,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  // Calculate values
  // Prevent "0" flash by checking loading/error states
  const showSkeleton = logsLoading || statsLoading;

  const totalIntake = Array.isArray(waterLogs)
    ? waterLogs.reduce((sum: number, log: any) => sum + log.volume, 0)
    : 0;

  // If we have an error, don't show "0", show fallback or null (handled in UI)
  const displayIntake = logsError ? null : totalIntake;

  const dailyTarget = resolveDailyTargetMl(userProfile);
  const waterIntakeL = displayIntake !== null ? displayIntake / 1000 : null;
  const goalL = resolveGoalLitres(userProfile);
  const percentage =
    displayIntake !== null ? Math.min(100, (displayIntake / dailyTarget) * 100) : 0;
  const timesDrank = trackingData?.successfulIntakes || waterLogs?.length || 0;
  // Issue 1 Fix: Calculate skipped as (reminderCount - successfulIntakes) instead of consecutiveMisses
  // consecutiveMisses resets to 0 on success, but we want total missed reminders
  const reminderCount = trackingData?.reminderCount || 0;
  const successfulIntakes = trackingData?.successfulIntakes || 0;

  // Active hours for time-based badges
  const activeHoursStart = userProfile?.activeHoursStart || '09:00';
  const activeHoursEnd = userProfile?.activeHoursEnd || '18:00';
  const activeHours = calculateActiveHours(activeHoursStart, activeHoursEnd);
  const reminderIntervalMin = userProfile?.reminderInterval || 45;
  const clockCompleted = completedReminderIntervals(
    activeHoursStart,
    activeHoursEnd,
    reminderIntervalMin,
    zonedMinutesOfDay(new Date(nowTick), userProfile?.timezone || 'Asia/Kolkata')
  );
  const expectedRemindersPerDay = resolveFrequencyGoal(userProfile);
  const frequencyTracking = reconcileFrequencyTracking({
    missCount: trackingData?.missCount ?? 0,
    successfulIntakes,
    reminderCount,
    completedIntervals: clockCompleted,
    pendingIntervals: pendingIntervalsSinceDeadline(
      trackingData?.nextDeadline,
      reminderIntervalMin,
      new Date(nowTick)
    ),
    totalIntervals: expectedRemindersPerDay,
  });

  // SIPS score — clock-aligned denominator (not extra opening-miss ticks)
  const { numerator: sipsNumerator, denominator: sipsDenominator } = getDynamicScoreParts({
    successfulIntakes,
    reminderCount: frequencyTracking.reminderCount,
  });

  // AVG VOL Numerator: totalIntake / reminderCount (skips count as 0ml)
  const avgVolumePerReminder = reminderCount > 0 ? Math.round(totalIntake / reminderCount) : 0;

  // AVG VOL Denominator: Use volumePerReminder set during onboarding
  // This ensures consistency with the recommendation shown during profile setup
  const targetVolumePerReminder = resolveVolumePerReminder(userProfile);

  // Calculate badges for Today's Badges card
  const missCount = frequencyTracking.missCount;
  const completedIntervals = frequencyTracking.reminderCount;
  const badgeResult = calculateBadges({
    dailyGoal: dailyTarget,
    totalReminderIntervals: expectedRemindersPerDay,
    completedIntervals,
    totalWaterLogged: displayIntake ?? 0,
    missCount,
  });
  const frequencyBadge = badgeResult.frequency;
  const volumeBadge = badgeResult.volume;
  const volumePercentage = badgeResult.volumePercentage;
  const timeBadge =
    TIME_BADGES.find((b) => activeHours >= b.minHours && activeHours < b.maxHours) ||
    TIME_BADGES[0];

  // Today's badges for display — Volume, then Frequency, then Time (locked order)
  const todaysBadges = [
    {
      emoji: volumeBadge.emoji,
      title: volumeBadge.name,
      description: `Volume: ${Math.round(volumePercentage)}% of goal`,
      color: 'bg-lilac-300',
      imageSrc: volumeBadge.imageSrc,
    },
    {
      emoji: frequencyBadge.emoji,
      title: frequencyBadge.name,
      description: `Frequency: ${frequencyBadge.description}`,
      color: 'bg-purple-300',
      imageSrc: frequencyBadge.imageSrc,
    },
    {
      emoji: timeBadge.emoji,
      title: `${timeBadge.name} Mode`,
      description: `Active: ${activeHours.toFixed(1)} hours`,
      color: 'bg-lilac-200',
    },
  ];

  // Live elapsed time since last drink (ms), null if no drinks today
  const lastDrinkAtMs = useMemo(() => {
    if (!Array.isArray(waterLogs) || waterLogs.length === 0) return null;
    const sorted = [...waterLogs].sort(
      (a: any, b: any) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime()
    );
    return new Date(sorted[0].loggedAt).getTime();
  }, [waterLogs]);

  const timeSinceLastDrinkLabel =
    lastDrinkAtMs == null ? null : formatElapsedSince(nowTick - lastDrinkAtMs);

  // Calculate time difference between consecutive sips
  const getTimeDiffBetweenLogs = (logs: any[]): Map<string, number> => {
    const diffMap = new Map<string, number>();
    if (!Array.isArray(logs) || logs.length < 2) return diffMap;

    const sortedLogs = [...logs].sort(
      (a: any, b: any) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime()
    );

    for (let i = 0; i < sortedLogs.length - 1; i++) {
      const currentTime = new Date(sortedLogs[i].loggedAt).getTime();
      const prevTime = new Date(sortedLogs[i + 1].loggedAt).getTime();
      const diffMinutes = Math.floor((currentTime - prevTime) / 60000);
      diffMap.set(sortedLogs[i].id || String(i), diffMinutes);
    }
    return diffMap;
  };
  const timeDiffMap = getTimeDiffBetweenLogs(waterLogs || []);

  const nickname = getDisplayName({
    ...(user as any),
    avatarName: userProfile?.avatarName || (user as any)?.profile?.avatarName,
  });

  const triggerHaptic = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate([50, 30, 50]);
    }
  }, []);

  const handleLogDrink = async (amount: number) => {
    // Trigger haptic feedback
    triggerHaptic();

    // Trigger animations
    setIsAnimating(true);
    setShowRipple(true);

    setTimeout(() => setIsAnimating(false), 500);
    setTimeout(() => setShowRipple(false), 600);

    // Log the water
    await logWater(amount, 'dashboard');

    setShowDrinkDialog(false);
    setSelectedPreset(null);
    setCustomAmount('');

    // Stay on the main dial. Recap only pins newest when that slide is already open.

    // Check if goal achieved
    const newTotal = totalIntake + amount;
    if (newTotal >= dailyTarget && totalIntake < dailyTarget) {
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100, 50, 100]);
      }
      toast({
        title: 'Goal achieved! 🎉',
        description: "You've reached your daily hydration goal!",
      });
    }
  };

  const sortedLogs = Array.isArray(waterLogs)
    ? [...waterLogs].sort(
        (a: any, b: any) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime()
      )
    : [];

  // Track carousel slide selection for dots indicator
  useEffect(() => {
    if (!carouselApi) return;

    const onSelect = () => {
      setSelectedSlide(carouselApi.selectedScrollSnap());
    };

    carouselApi.on('select', onSelect);
    onSelect(); // Set initial state

    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi]);

  // Keep newest drink visible when logs change on Today's Recap
  const prevLogCountRef = useRef(0);
  const prevRecapSlideRef = useRef(false);
  useEffect(() => {
    if (selectedSlide !== 1 || !recapListRef.current) {
      prevRecapSlideRef.current = selectedSlide === 1;
      return;
    }
    const count = Array.isArray(waterLogs) ? waterLogs.length : 0;
    const enteredSlide = !prevRecapSlideRef.current;
    const newDrinkLogged = count !== prevLogCountRef.current;
    if (enteredSlide || newDrinkLogged) {
      recapListRef.current.scrollTop = 0;
      recapUserPausedRef.current = true;
      if (recapPauseTimerRef.current) {
        window.clearTimeout(recapPauseTimerRef.current);
      }
      recapPauseTimerRef.current = window.setTimeout(() => {
        recapUserPausedRef.current = false;
      }, 2500);
    }
    prevLogCountRef.current = count;
    prevRecapSlideRef.current = true;
  }, [selectedSlide, waterLogs]);

  // Gentle auto-scroll through Today's Recap when content overflows
  useEffect(() => {
    if (selectedSlide !== 1) return;
    const el = recapListRef.current;
    if (!el) return;

    const pauseAutoScroll = () => {
      recapUserPausedRef.current = true;
      if (recapPauseTimerRef.current) {
        window.clearTimeout(recapPauseTimerRef.current);
      }
      recapPauseTimerRef.current = window.setTimeout(() => {
        recapUserPausedRef.current = false;
      }, 4000);
    };

    el.addEventListener('wheel', pauseAutoScroll, { passive: true });
    el.addEventListener('touchstart', pauseAutoScroll, { passive: true });
    el.addEventListener('pointerdown', pauseAutoScroll);

    const intervalId = window.setInterval(() => {
      if (!el || recapUserPausedRef.current) return;
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll <= 8) return;

      if (el.scrollTop >= maxScroll - 1) {
        el.scrollTop = 0;
      } else {
        el.scrollTop += 1;
      }
    }, 50);

    return () => {
      window.clearInterval(intervalId);
      el.removeEventListener('wheel', pauseAutoScroll);
      el.removeEventListener('touchstart', pauseAutoScroll);
      el.removeEventListener('pointerdown', pauseAutoScroll);
      if (recapPauseTimerRef.current) {
        window.clearTimeout(recapPauseTimerRef.current);
      }
    };
  }, [selectedSlide, waterLogs]);

  const handleSubmitDialog = () => {
    const amount = selectedPreset ?? parseInt(customAmount);
    if (amount > 0 && amount <= 2000) {
      handleLogDrink(amount);
    }
  };

  const presets = [100, 200, 300];
  const percentInt = Math.round(percentage);

  const activeDays: string[] = userProfile?.activeDays || [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
  ];

  const weekAnalytics = useMemo(
    () => buildWeekAnalytics(activeDays, weeklyStats || [], dailyTarget),
    [activeDays, weeklyStats, dailyTarget]
  );

  const insights = generateInsights({
    weeklyStats: weeklyStats || [],
    totalIntakeToday: totalIntake,
    dailyTarget,
    avgVolumePerReminder,
    targetVolumePerReminder,
    volumeHits: weekAnalytics.volumeHits,
    frequencyHits: weekAnalytics.frequencyHits,
    activeDaysInWindow: weekAnalytics.days.length,
    weeklyAverageL: weekAnalytics.weeklyAverageL,
  });

  const slideTitles = ['Log your drink', "Today's Recap", 'Insights'];

  const formatTime = (date: Date) =>
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

  if (isLoading || statsLoading || logsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-lilac-300 border-t-purple-500 mx-auto mb-4"></div>
          <SwygWaveMark size="lg" className="mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-28 bg-cream">
      <RealTimeCelebration />

      <div className="max-w-md mx-auto px-5 pt-4 pb-1 space-y-3">
        <BrandHeader title="Mainstage" nickname={nickname} />

        <Carousel setApi={setCarouselApi} className="w-full">
          <CarouselContent className="-mt-2 pb-2">
            <CarouselItem className="overflow-visible">
              <MainstageSlideCard title={slideTitles[0]}>
                <div className="flex justify-between items-center mb-2 gap-2">
                  <div className="relative px-4 py-2 min-w-0 flex-1">
                    <div className="absolute inset-0 bg-lilac-300/60 rounded-[50%] -rotate-6" />
                    <div className="relative text-center">
                      <div
                        className="text-xl font-bold text-deep-800 tabular-nums"
                        data-testid="text-sips-counter"
                      >
                        {sipsNumerator}/{sipsDenominator}
                      </div>
                      <div className="text-[11px] tracking-wider text-deep-500 font-medium">
                        SIPS TODAY
                      </div>
                    </div>
                  </div>
                  <div className="relative px-4 py-2 min-w-0 flex-1">
                    <div className="absolute inset-0 bg-lilac-300/60 rounded-[50%] rotate-6" />
                    <div className="relative text-center">
                      <div
                        className="text-xl font-bold text-deep-800 tabular-nums"
                        data-testid="text-avg-volume"
                      >
                        {reminderCount > 0 ? avgVolumePerReminder : '---'}
                      </div>
                      <div className="text-[11px] tracking-wider text-deep-500 font-medium">
                        AVG VOL
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center shrink-0 py-1 mb-1 relative">
                  <div
                    className={`relative w-44 h-44 shrink-0 ${isAnimating ? 'animate-drink-pulse' : ''}`}
                  >
                    <div className="absolute inset-0 rounded-full bg-lilac-300/50" />
                    {showRipple && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-full h-full rounded-full bg-purple-400/30 animate-ripple" />
                      </div>
                    )}
                    <div className="absolute inset-4 bg-white rounded-full shadow-inner flex flex-col items-center justify-center z-10 px-2">
                      <span
                        className={`text-3xl font-bold text-deep-800 leading-none ${isAnimating ? 'animate-number-pop' : ''}`}
                      >
                        {waterIntakeL !== null ? waterIntakeL.toFixed(2) : '---'}
                        <span className="text-deep-300">/</span>
                      </span>
                      <span className="text-deep-400 text-sm mt-0.5">
                        {goalL.toFixed(1)} litres
                      </span>
                    </div>
                    <div className="absolute right-0 top-8 z-10">
                      <WavyBadge size="md">
                        <span className="block text-[11px] leading-tight">
                          {percentInt}%<br />
                          Done
                        </span>
                      </WavyBadge>
                    </div>
                  </div>
                </div>

                <div className="mb-2 text-center">
                  <p className="text-[11px] tracking-wider text-deep-500 font-medium mb-0.5">
                    TIME SINCE LAST DRINK
                  </p>
                  <p
                    className="text-xl font-bold text-deep-800 tabular-nums"
                    data-testid="text-time-since-last-drink"
                  >
                    {timeSinceLastDrinkLabel ?? '—'}
                  </p>
                </div>

                <div className="mt-auto space-y-1.5">
                  <Button
                    onClick={() => setShowDrinkDialog(true)}
                    disabled={!isWithinActiveHours || isLogging}
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full py-5 text-base font-semibold shadow-[0_4px_0_hsl(var(--purple-800))] disabled:opacity-50"
                    data-testid="button-log-drink"
                  >
                    {isLogging ? 'Logging...' : 'Clock It'}
                  </Button>

                  {!isWithinActiveHours && (
                    <p className="text-center text-xs text-deep-400">
                      Logging available during active hours only
                    </p>
                  )}

                  <p className="text-center text-xs text-deep-400">Swipe For Logs And Badges</p>
                </div>
              </MainstageSlideCard>
            </CarouselItem>

            <CarouselItem className="overflow-visible">
              <MainstageSlideCard title={slideTitles[1]}>
                <div className="flex justify-center shrink-0 py-1 mb-2 relative">
                  <div className="relative w-40 h-40 shrink-0">
                    <div className="absolute inset-0 rounded-full bg-lilac-300/50" />
                    <div className="absolute inset-3 bg-white rounded-full flex flex-col items-center justify-center z-10 px-1">
                      <span className="text-3xl font-bold text-deep-800 leading-none">
                        {waterIntakeL !== null ? waterIntakeL.toFixed(2) : '---'}
                        <span className="text-deep-300">/</span>
                      </span>
                      <span className="text-deep-400 text-sm mt-0.5">
                        {goalL.toFixed(1)} litres
                      </span>
                    </div>
                    <div className="absolute right-0 top-4 z-10">
                      <WavyBadge size="sm">
                        <span className="block text-[10px] leading-tight">
                          {percentInt}%<br />
                          Done
                        </span>
                      </WavyBadge>
                    </div>
                  </div>
                </div>

                <div
                  ref={recapListRef}
                  className="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-3 px-5 pb-1"
                >
                  {sortedLogs.length === 0 ? (
                    <p className="text-center text-sm text-deep-400 py-6">No drinks logged yet</p>
                  ) : (
                    sortedLogs.map((log: any, idx: number) => {
                      const badgeLeft = idx % 2 === 0;
                      const logTime = new Date(log.loggedAt);
                      return (
                        <div
                          key={log.id || idx}
                          data-recap-log
                          className="relative bg-hydro-200/70 rounded-full min-h-12 flex items-center px-4 py-2 overflow-hidden"
                        >
                          {badgeLeft && (
                            <div className="absolute left-1 top-1/2 -translate-y-1/2 z-10">
                              <WavyBadge size="sm">
                                <span className="text-[9px] font-bold leading-none">
                                  {log.volume}ML
                                </span>
                              </WavyBadge>
                            </div>
                          )}
                          <span
                            className={`text-sm font-semibold text-deep-700 w-full text-center truncate ${badgeLeft ? 'pl-14' : 'pr-14'}`}
                          >
                            {formatTime(logTime)}
                          </span>
                          {!badgeLeft && (
                            <div className="absolute right-1 top-1/2 -translate-y-1/2 z-10">
                              <WavyBadge size="sm">
                                <span className="text-[9px] font-bold leading-none">
                                  {log.volume}ML
                                </span>
                              </WavyBadge>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </MainstageSlideCard>
            </CarouselItem>

            <CarouselItem>
              <MainstageSlideCard title={slideTitles[2]}>
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-5">
                  {insights.map((ins) => (
                    <div key={ins.n} className="relative px-4 py-5 overflow-hidden">
                      <div
                        className={`absolute inset-0 bg-lilac-300/60 ${
                          ins.align === 'left'
                            ? 'rounded-[60%_40%_70%_30%/40%_50%_50%_60%]'
                            : 'rounded-[40%_60%_30%_70%/50%_40%_60%_50%]'
                        }`}
                      />
                      <div
                        className={`absolute z-10 ${
                          ins.align === 'left' ? 'left-1 top-6' : 'right-1 bottom-6'
                        }`}
                      >
                        <WavyBadge size="sm">
                          <span className="text-sm font-extrabold">{ins.n}</span>
                        </WavyBadge>
                      </div>
                      <div
                        className={`relative min-w-0 ${ins.align === 'left' ? 'pl-14 pr-4' : 'pr-14 pl-4'}`}
                      >
                        <h3 className="font-bold text-deep-800 text-base mb-1 break-words">
                          {ins.title}
                        </h3>
                        <p className="text-sm text-deep-500 leading-relaxed break-words">
                          {ins.body}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </MainstageSlideCard>
            </CarouselItem>
          </CarouselContent>
        </Carousel>

        <div className="flex justify-center gap-2 pt-1">
          {slideTitles.map((_, idx) => (
            <button
              key={idx}
              onClick={() => carouselApi?.scrollTo(idx)}
              className={`h-2 rounded-full transition-all ${
                idx === selectedSlide ? 'w-2 bg-purple-600' : 'w-2 bg-deep-200'
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>

        <ErrorBoundary fallback={<div className="pt-2 h-24" aria-hidden="true" />}>
          <NewsCarousel />
        </ErrorBoundary>
      </div>

      <Dialog
        open={showDrinkDialog}
        onOpenChange={(open) => {
          setShowDrinkDialog(open);
          if (!open) {
            setSelectedPreset(null);
            setCustomAmount('');
          }
        }}
      >
        <DialogContent className="bg-cream border-deep-200 rounded-3xl max-w-sm mx-auto p-6">
          <h2 className="text-xl font-bold text-deep-800 mb-5 pr-8 leading-snug">
            How much water did you drink?
          </h2>

          <div className="bg-hydro-200/60 rounded-2xl p-4 mb-5">
            <p className="text-center text-xs text-deep-500 mb-1">Enter volume</p>
            <Input
              type="number"
              placeholder="400 ml"
              value={customAmount}
              onChange={(e) => {
                setCustomAmount(e.target.value);
                setSelectedPreset(null);
              }}
              className="bg-transparent border-0 text-center text-2xl font-semibold text-deep-800 placeholder:text-deep-300 focus-visible:ring-0 shadow-none p-0 h-auto"
              min="1"
              max="2000"
              data-testid="input-custom-amount"
            />
          </div>

          <div className="flex justify-around mb-6">
            {presets.map((p) => (
              <WavyBadge
                key={p}
                size="lg"
                filled={selectedPreset === p}
                onClick={() => {
                  setSelectedPreset(p);
                  setCustomAmount('');
                }}
              >
                <span className="font-bold">{p}ml</span>
              </WavyBadge>
            ))}
          </div>

          <Button
            onClick={handleSubmitDialog}
            disabled={
              isLogging || (!selectedPreset && (!customAmount || parseInt(customAmount) <= 0))
            }
            className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full py-6 text-lg font-semibold shadow-[0_4px_0_hsl(var(--purple-800))]"
            data-testid="button-log-custom"
          >
            Log a drink
          </Button>
        </DialogContent>
      </Dialog>

      <ErrorBoundary fallback={null}>
        <FeaturedArticleOverlay />
      </ErrorBoundary>
      <Navigation />
    </div>
  );
}
